// @flow
import React, { useCallback, useState, useEffect } from "react";
import { type TreeItemDTO } from "data/types";
import Tree from "components/Tree";
import styles from "./FolderTree.module.css";
import Row from "./Row";

type Props = {
    /** current node id to mark as active */
    activeTreeNodeId: number,
    /** tree items */
    tree: Array<TreeItemDTO>,
    /** callback when user clicks a tree item
     * @param {TreeItemDTO} treeItem tree item's object the user clicked
     * @param {SyntheticMouseEvent<*>} e HTML event object
     */
    onClick: (treeItem: TreeItemDTO, e: SyntheticMouseEvent<*>) => void,
    /** callback when user double-click a tree item
     * @param {TreeItemDTO} treeItem tree item's object the user clicked
     */
    onDoubleClick: (treeItem: TreeItemDTO) => void,
    /** callback when user expands a tree item
     * @param {TreeItemDTO} treeItem tree item's object the user clicked
     * @param {SyntheticMouseEvent<*>} e HTML event object
     */
    onExpand: (treeItem: TreeItemDTO, e: SyntheticMouseEvent<*>) => void,
    /** callback when user wants to load more results */
    onLoadMore: () => void,
    /** filter text currently applied */
    filter?: string,
    /** optional NoData Content Component */
    NoDataContent: Element<any>,
    /** total number of results available */
    totalCount: number,
    /** whether list is currently focused
     * @default {boolean} true
     */
    focused?: boolean,
    /** view name */
    viewName: string,
    /** next page number */
    nextPage: boolean | ?string,
    /** previous page number */
    prevPage: boolean | ?string,
};

const FolderTree = ({
    activeTreeNodeId,
    tree,
    onClick,
    onDoubleClick,
    onExpand,
    onLoadMore,
    filter,
    NoDataContent,
    totalCount,
    focused = true,
    viewName,
    status,
    nextPage = null,
    prevPage = null,
}: Props) => {
    const hasNext = !!(tree && nextPage != null);
    const hasPrev = !!(tree && prevPage != null);
    const [activeTreeNodeIndex, setActiveTreeNodeIndex] = useState(-1);

    const currentActiveTreeNodeIndex =
        tree?.findIndex(
            (row) => row.id === decodeURIComponent(activeTreeNodeId)
        ) + (prevPage != null ? 1 : 0) ?? /* istanbul ignore next */ -1;

    useEffect(() => {
        setActiveTreeNodeIndex(currentActiveTreeNodeIndex);
    }, [currentActiveTreeNodeIndex]);

    const handleScroll = useCallback(
        (e) => {
            if (activeTreeNodeIndex === -1) return;
            setActiveTreeNodeIndex(-1);
        },
        [activeTreeNodeIndex]
    );

    const handleLoadMore = useCallback(() => onLoadMore(), [onLoadMore]);
    const handleLoadPrev = useCallback(
        () => onLoadMore(prevPage),
        [onLoadMore, prevPage]
    );

    const renderRow = useCallback(
        ({
            key, // Unique key within array of rows
            index, // Index of row within collection
            isScrolling, // The List is currently being scrolled
            isVisible, // This row is visible within the List (eg it is not an overscanned row)
            style, // Style object to be applied to row (to position it)
        }: {
            key: number,
            index: number,
            isScrolling: boolean,
            isVisible: boolean,
            style: StyleSheet,
        }) => (
            <Row
                key={key}
                index={index}
                isScrolling={isScrolling}
                isVisible={isVisible}
                style={style}
                hasNext={hasNext}
                hasPrev={hasPrev}
                tree={tree}
                onLoadMore={handleLoadMore}
                onLoadPrev={handleLoadPrev}
                activeTreeNodeId={activeTreeNodeId}
                onDoubleClick={onDoubleClick}
                onClick={onClick}
                onExpand={onExpand}
                filter={filter}
                itemUri={tree[index]?.itemUri}
                viewName={viewName}
                status={status}
            />
        ),
        [
            tree,
            onClick,
            onDoubleClick,
            onExpand,
            handleLoadMore,
            handleLoadPrev,
            filter,
            activeTreeNodeId,
            hasNext,
            hasPrev,
            viewName,
            status,
        ]
    );
    /* istanbul ignore next */
    const totalItems = tree
        ? tree.length + (hasNext ? 1 : 0) + (hasPrev ? 1 : 0)
        : 0;

    return (
        <Tree
            className={focused === false ? styles.unfocused : ""}
            filter={filter}
            activeTreeNodeId={activeTreeNodeId}
            scrollToIndex={activeTreeNodeIndex}
            totalItems={totalItems}
            renderRow={renderRow}
            locContext="folder"
            NoDataContent={NoDataContent}
            onScroll={handleScroll}
        />
    );
};

export default FolderTree;
